<template>
  <div class="signup-simple-form">
    <h2 class="text-campton-bold">{{ $gettext('Start your free account now') }}</h2>
    <b-form @submit.prevent="submitSignup" class="gk-clear-form">
      <div class="country-selector">
        <div class="text-campton-book">
          {{ $gettext('Business location*') }}
        </div>
        <div class="country-selector__selected isoflags-f32" @click="showCountrySelector = true" :class="{ hidden: showCountrySelector }">
          
          <div class="flag" :class="[businesslocation.toLowerCase()]" />
          <div>
            {{ countryOptions[businesslocation] }}
            <font-awesome-icon icon="fa-solid fa-chevron-down" />
          </div>
        </div>
        <b-form-group id="fieldset-signup-country" class="country-selector__selector" :class="{ visible: showCountrySelector }">
          <b-form-select
            id="input-signup-country" 
            v-model="businesslocation" 
            :options="formCountryOptions"
            @change="showCountrySelector = false"
            required
          />
        </b-form-group>
      </div>

      <b-form-group id="fieldset-signup-email" :label="$gettext('Email address*')" label-for="input-signup-email">
        <div class="position-relative">
          <b-form-input 
            type="email"
            id="input-signup-email"
            :placeholder="randomPlaceholderEmail"
            v-model="email"
            trim required
          />
        </div>
      </b-form-group>

      <p v-if="showEmailWarning" class="text-danger text-center">
        <font-awesome-icon icon="fa-regular fa-warning" />
        {{ emailWarningTxt }}
      </p>

      <b-form-group id="fieldset-signup-password" :label="$gettext('Password*')" label-for="input-signup-password">
        <div class="position-relative">
          <b-form-input
            type="password"
            id="input-signup-password"
            v-model="password"
            trim required
            @focus="showPasswordHint = true"
            @blur="() => { showPasswordHint = (password.length > 0) }"
          />
          <ShowPasswordIcon />
        </div>
        <PasswordStrengthHint :show="showPasswordHint" :password="password" @score-changed="(score) => { passwordScore = score }" />
      </b-form-group>

      <div style="padding-top: 0.5rem; width: 100%;">
        <GekkoButton
          type="submit"
          :text="$gettext('Get started')"
          :loading="loadingData || savingData"
          :disabled="(passwordScore < 1)"
          variant="primary"
          class="login-btn text-campton-book position-relative"
        />
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import { randomCelebName, randomEmail } from "@/utils/"

import GekkoButton from "@/components/GekkoButton"
import ShowPasswordIcon from "@/components/ui/ShowPasswordIcon"
import PasswordStrengthHint from "@/components/ui/PasswordStrengthHint"

export default {
  name: 'SignupFormSimple',
  components: {
    GekkoButton,
    ShowPasswordIcon,
    PasswordStrengthHint
  },
  data () {
    return {
      email: '',
      businesslocation: '',
      password: '',
      showPasswordHint: false,
      showEmailWarning: false,
      emailWarningTxt: '',
      passwordScore: 0,
      signupStep: 1,
      loading: false,
      loadingCountries: false,
      loadingUserLocation: false,
      savingData: false,
      showCountrySelector: false
    }
  },
  computed: {
    ...mapGetters({
      countryOptions: 'main/getCountryOptions',
      supportedCountries: 'signup/getSupportedCountries',
      userCountry: 'signup/getUserCountry',
      userLanguage: 'signup/getUserLanguage',
    }),
    randomPlaceholderName () {
      return randomCelebName()
    },
    randomPlaceholderEmail () {
      return randomEmail(this.randomPlaceholderName)
    },
    formCountryOptions () {
      const extracted = {}
      const remaining = {}
      for (const key in this.countryOptions) {
        if (this.supportedCountries.includes(key)) {
          extracted[key] = this.countryOptions[key]
        } else {
          remaining[key] = this.countryOptions[key]
        }
      }
      return [
        { label: this.$gettext('Supported countries'), options: extracted },
        { label: this.$gettext('Others'), options: remaining }
      ]
    },
    loadingData () {
      return this.loading || this.loadingCountries || this.loadingUserLocation
    },
  },
  created () {
    this.setupData()
  },
  mounted () {
  },
  methods: {
    ...mapActions({ 
      saveData: 'signup/signUp',
      checkAvailability: 'signup/checkAvailability',
      loadCountries: 'main/loadCountries',
      geoLocateUserIp: 'signup/geoLocateUserIp',
      getAccessToken: "auth/getAccessToken"
    }),
    setupData () {
      console.log('this.$route.params.language', this.$route.params.language)
      // geo locate user
      this.loadingUserLocation = true
      this.geoLocateUserIp().then(() => {
        this.preferredlanguage = this.$route.params.language || this.userLanguage
        this.businesslocation = this.userCountry
        this.loadingUserLocation = false
      })

      // load countries
      if (this.countryOptions.length < 1) {
        this.loadingCountries = true
        this.loadCountries().then(() => {
          this.loadingCountries = false
          console.log('countries loaded', this.countryOptions)
        })
      } else {
        this.loadingCountries = false
        console.log('countries already loaded', this.countryOptions)
      }

    },
    submitSignup () {
      this.loading = true
      return this.checkAvailability(this.email).then((response) => {
        this.showEmailWarning = false
        console.log('response', response)
        if (response.status === 200) {
          if (this.passwordScore > 0) {
            const data = {
              email: this.email,
              password: this.password,
              country: this.businesslocation,
              preferred_language: this.preferredlanguage
            }
            this.savingData = true
            this.saveData(data).then(() => {
              console.log('saved data, trying login')
              this.login()
            }).catch((error) => {
              console.log('error', error)
            }).finally(() => {
              this.savingData = false
            })
          } else {
            console.log('bad password')
          }
        }
      }).catch((error) => {
        if (error.response.status === 409) {
          this.showEmailWarning = true
          this.emailWarningTxt = this.$gettext('This email address already exists')
        } else if (error.response.status === 422) {
          this.showEmailWarning = true
          this.emailWarningTxt = this.$gettext('That email doesn\'t seem to be ok')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    login () {
      this.loading = true
      this.getAccessToken({
        'username': this.email,
        'password': this.password,
      }).then((response) => {
        console.log('Login.vue::login() -> getAccessToken THEN response', response)
        this.$emit('authorised', false)
      }).catch((error) => {
        console.log('error authenticating new user', error)
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.signup-simple-form {
  .country-selector {
    position: relative;
    &__selected {
      font-size: 18px;
      display: flex;
      gap: 0.5rem;
      align-items: center;
      opacity: 1;
      &.hidden { display: none; opacity: 0; }
      .flag { display: block; width: 32px; height: 32px;  }
      margin-bottom: 1.25rem;
      position: absolute;
      .fa-chevron-down {
        font-size: 14px;
      }
    }
    &__selector {
      .custom-select {
        cursor: pointer;
      }
      // display: none;
      opacity: 0;
      &.visible { display: block; opacity: 1; }
    }
    
  }
}
</style>